import React, { createRef, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Divider, Modal, Spacer, Spinner, Typography } from '@telus-uds/components-web';

import FormField from './formField';
import UserDetailsForm from './userDetailsForm';
import FormButtons from '../ContactCentre/formButtons';
import FormSummaryGroup from './formSummaryGroup';
import request, { isAdmin } from '../utils/request';
import ServiceBusinessConnect from '../ContactCentre/serviceBusinessConnect';
import ServiceContactCentre from '../ContactCentre/serviceContactCentre';
import AdditionalServices from '../ContactCentre/additionalServices';

import { Box } from '../design/css';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import {
  normalizedFields,
  getFieldName,
  getFieldValue,
  getFieldType,
  getFieldChecked
} from './utils/formCommon';
import {
  businessConnectContactCentreOnlineTool,
  businessConnectContactCentreLandingPage,
  fieldCcContractTerm
} from '../utils/constant';

const formSection = 'formSection';
const userDetailsKey = 'userDetails';

const FormStepContactCentre = ({
  stepData,
  onFormSubmit,
  onSaveAndExit,
  orderData,
  setOrderData,
  formName,
  createNewContactOrder,
  navigateToContactCentreHomePage
}) => {
  if (!stepData) return null;
  const {
    fields: { name: formStepName, formStepFields }
  } = stepData;
  const [isLoading, setIsLoading] = useState(true);
  const [modalData, setModalData] = useState({
    title: '',
    body: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    open: false
  });
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [formFields, setFormFields] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [businessConnectServices, setBusinessConnectServices] = useState(() => {
    return _.get(orderData, 'businessConnectServices', []);
  });
  const [contactCentreServices, setContactCentreServices] = useState(() => {
    return _.get(orderData, 'contactCentreServices', []);
  });
  const [additionalServices, setAdditionalServices] = useState(() => {
    return _.get(orderData, 'additionalServices', []);
  });
  const [showFooterError, setShowFooterError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const localizedStrings = useLocalizedStringsContext();

  const {
    modalForm: { fieldRequiredMessage },
    formButtons: { newRequestButtonLabel, submittingButtonLabel },
    callCentre: {
      modal: { modalHeading, modalBody, modalConfirmText }
    },
    contentfulValues: { oneYears }
  } = localizedStrings;

  const formStepRef = createRef();

  const isRelatedFieldExisted = (field, fields) => {
    const {
      appearsAfterField,
      appearsAfterValue,
      backendFieldValidation
    } = field;
    if (!appearsAfterField && !backendFieldValidation) return true;

    let result = true;
    let i = 0;
    if (appearsAfterField) {
      while (i < appearsAfterField.length && result) {
        const relatedFieldName = getFieldName(
          appearsAfterField[i],
          orderData,
          ''
        );
        const relatedFieldType = getFieldType(relatedFieldName, fields);
        const relatedFieldValue =
          relatedFieldType === 'Checkbox'
            ? getFieldChecked(relatedFieldName, fields)
            : getFieldValue(relatedFieldName, fields);
        if (relatedFieldName) {
          if (
            fields[relatedFieldName] &&
            fields[relatedFieldName].isVisible &&
            relatedFieldValue
          ) {
            if (appearsAfterValue) {
              let isValueSelected;
              if (_.isArray(relatedFieldValue)) {
                isValueSelected = appearsAfterValue.find(item =>
                  relatedFieldValue.includes(item.fields.value)
                );
              } else {
                isValueSelected = appearsAfterValue.find(
                  item => relatedFieldValue === item.fields.value
                );
              }
              result = result && typeof isValueSelected !== 'undefined';
            }
          } else if (orderData && orderData[relatedFieldName]) {
            if (fields[relatedFieldName]) {
              if (
                (isFormTouched &&
                  orderData[relatedFieldName] === relatedFieldValue) ||
                !isFormTouched
              ) {
                if (appearsAfterValue) {
                  const isValueSelected = appearsAfterValue.find(
                    item => orderData[relatedFieldName] === item.fields.value
                  );
                  result = result && typeof isValueSelected !== 'undefined';
                }
              } else result = false;
            } else if (appearsAfterValue) {
              const isValueSelected = appearsAfterValue.find(
                item => orderData[relatedFieldName] === item.fields.value
              );
              result = result && typeof isValueSelected !== 'undefined';
            }
          } else {
            result = false;
          }
        }
        i += 1;
      }
    }

    if (backendFieldValidation) {
      let isVisible = result;
      for (let index = 0; index < backendFieldValidation.length; index += 1) {
        const {
          fields: { key, value }
        } = backendFieldValidation[index];
        isVisible =
          isVisible &&
          (value.split('|').includes(orderData[key]) ||
            (value.split('|').includes('undefined') &&
              typeof orderData[key] === 'undefined'));
      }
      return isVisible;
    }

    return result;
  };

  useEffect(() => {
    if (formStepFields) {
      const updatedFields = normalizedFields(
        formStepFields,
        formFields,
        orderData,
        false,
        localizedStrings
      );

      setFormFields(updatedFields);
      setIsLoading(false);
      setIsFormTouched(false);
      if (formStepRef && formStepRef.current)
        formStepRef.current.scrollTo(0, 0);
    }
  }, [formStepFields, orderData]);

  const getErrors = (data, errors) => {
    let result = errors;

    if (!data) return result;
    data.forEach(({ sys, fields }) => {
      const {
        contentType: {
          sys: { id }
        }
      } = sys;
      const key = fields.name;
      const { value, isRequired, isVisible, ref } =
        formFields &&
        Object.prototype.hasOwnProperty.call(formFields, key) &&
        formFields[key];

      if (id === formSection) {
        result = getErrors(fields.formFields, result);
      } else if (id === userDetailsKey) {
        result = getErrors(fields.formStepFields, result);
      } else if (isVisible) {
        if (isRequired) {
          if (!value && isRelatedFieldExisted(fields, formFields)) {
            result[key] = { text: fieldRequiredMessage, ref };
          } else {
            delete result[key];
          }
        }

        if (fields.fieldValidation && fields.fieldValidation.fields) {
          if (
            typeof fields.appearsAfterField === 'undefined' ||
            (typeof fields.appearsAfterField !== 'undefined' &&
              isRelatedFieldExisted(fields, formFields))
          ) {
            const {
              errorMessage,
              logic,
              value: expectedValue
            } = fields.fieldValidation.fields;
            let hasError = false;
            switch (logic) {
              case 'equal':
                // eslint-disable-next-line eqeqeq
                hasError = !expectedValue.split('|').includes(value);
                break;
              case 'regex':
                {
                  const regexLogic = new RegExp(expectedValue);
                  hasError = !regexLogic.test(value);
                }
                break;
              case 'gt':
                hasError = parseInt(value, 10) < parseInt(expectedValue, 10);
                break;
              case 'gte':
                hasError = parseInt(value, 10) <= parseInt(expectedValue, 10);
                break;
              case 'lt':
                hasError = +value < +expectedValue;
                break;
              default:
            }
            if (hasError) result[key] = { text: errorMessage, ref };
          }
        }
      }
    });
    return result;
  };

  const getFormData = () => {
    const result = Object.keys(formFields)
      .filter(key => typeof formFields[key].value !== 'undefined')
      .reduce((acc, key) => {
        const { type, value: oldValue, checked, isVisible } = formFields[key];
        const value = isVisible ? oldValue : '';

        acc[key] = type === 'Checkbox' ? checked : value;
        return acc;
      }, {});

    result.businessConnectServices = businessConnectServices;
    result.contactCentreServices = contactCentreServices;
    result.additionalServices = additionalServices;

    return result;
  };

  const saveAndExit = () => {
    const fields = getFormData();
    if (Object.entries(fields).length) {
      onSaveAndExit({ ...fields });
    }
  };

  const submit = e => {
    if (isSubmitting) return;
    e.preventDefault();
    const errors = getErrors(formStepFields, { ...formErrors });
    setFormErrors(errors);

    if (Object.entries(errors).length === 0) {
      setIsSubmitting(true);
      const fields = getFormData();
      setIsLoading(true);
      onFormSubmit({ ...fields }, orderData)
        .then(() => {
          setIsLoading(false);
          setModalData({
            ...modalData,
            title: modalHeading,
            body: modalBody,
            confirmText: modalConfirmText,
            open: true
          });
          setTimeout(() => setIsSubmitting(false), 2000);
        })
        .catch(() => {
          setIsLoading(false);
          setIsSubmitting(false);
        });
    } else {
      setShowFooterError(true);
    }
  };

  const handleFormField = ({ name, value, checked }) => {
    setIsFormTouched(true);
    const fields = { ...formFields };
    fields[name] = {
      ...fields[name],
      value,
      checked,
      isVisible: true,
      touched: true
    };
    setShowFooterError(false);
    setFormFields({ ...fields });
  };

  const renderFormSummary = (formSummaryGroup, data = orderData) => {
    const profile = request.getProfile();
    const summaryData = { ...profile, ...data };
    return <FormSummaryGroup data={formSummaryGroup} orderData={summaryData} />;
  };

  const renderFormFields = data =>
    data &&
    data.map(({ sys, fields }, index) => {
      const {
        contentType: {
          sys: { id }
        },
        id: sysId
      } = sys;
      const { name: fieldName, title, expiredDate, type } = fields;
      const fieldValue = getFieldValue(fieldName, formFields);
      const fieldDateConfig = {};
      let selectOptions;

      if (type === 'MultiCheckbox') {
        selectOptions = formFields[fieldName].options;
      }

      const error = formErrors[fieldName];
      const key = `FormField${fieldName}${index}${sysId}`;
      let result;

      if (expiredDate && new Date(expiredDate) < new Date()) return null;
      if (!isRelatedFieldExisted(fields, formFields)) return null;

      if (fieldName === fieldCcContractTerm && !isAdmin) {
        selectOptions = fields.options.filter(
          item => ![oneYears].includes(item.fields.value)
        );
      }

      if (id === formSection) {
        const { formSummaryGroup } = fields;
        result = (
          <>
            <Typography heading="h2" variant={{ size: 'h2' }} block>
              {title}
            </Typography>
            {formSummaryGroup
              ? renderFormSummary(formSummaryGroup)
              : renderFormFields(fields.formFields)}
          </>
        );
      } else if (id === userDetailsKey) {
        result = (
          <UserDetailsForm
            key={key}
            data={fields}
            setFormFields={setFormFields}
            orderData={orderData}
            formErrors={formErrors}
            formFields={formFields}
            isFormTouched={isFormTouched}
            setIsFormTouched={setIsFormTouched}
            resetSelectedFields={() => {}}
          />
        );
      } else {
        let ref;
        let isDisabled;
        if (formFields[fieldName]) {
          ({ ref, isDisabled } = formFields[fieldName]);
        }

        const fieldData = {
          ...fields,
          value: fieldValue,
          options: selectOptions || fields.options,
          ref,
          name: fieldName,
          disabled: isDisabled,
          sysId,
          onChange: (e, isFieldModified, fileUploded) =>
            handleFormField(e.target, isFieldModified, fileUploded),
          fieldDateConfig,
          formFields,
          setFormFields,
          setOrderData
        };

        result = (
          <>
            <FormField
              key={key}
              orderData={orderData}
              data={fieldData}
              error={error && error.text}
            />
          </>
        );
      }

      return result;
    });

  const goToHomePage = () => {
    setModalData({ ...modalData, open: false });
    navigateToContactCentreHomePage();
  };

  const handleCreateNewOrder = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      createNewContactOrder();
    } finally {
      setTimeout(() => setIsSubmitting(false), 2000);
    }
  };

  return (
    <div ref={formStepRef}>
      <Spinner fullScreen={isLoading} label="Loading..." show={isLoading} />
      <div
        style={{ backgroundColor: '#fff', width: '1200px', marginTop: '4rem' }}
      >
        <form
          name={formStepName}
          onSubmit={e => e.preventDefault()}
          encType="multipart/form-data"
        >
          {formName === businessConnectContactCentreOnlineTool && (
            <Box
              style={{
                border: '1px solid #e3e3e3',
                boxShadow: '1px 1px 9px 4px #e3e3e3',
                padding: '2rem',
                marginBottom: '3rem'
              }}
            >
              <div className="expand">{renderFormFields(formStepFields)}</div>
              {formName === businessConnectContactCentreOnlineTool && (
                <>
                  <ServiceContactCentre
                    contactCentreServices={contactCentreServices}
                    setContactCentreServices={setContactCentreServices}
                    setIsFormTouched={setIsFormTouched}
                  />
                  <Divider space={2} variant={{ inverse: true }} />
                  <ServiceBusinessConnect
                    businessConnectServices={businessConnectServices}
                    setBusinessConnectServices={setBusinessConnectServices}
                    setIsFormTouched={setIsFormTouched}
                  />
                  <Divider space={2} variant={{ inverse: true }} />
                  <AdditionalServices
                    additionalServices={additionalServices}
                    setAdditionalServices={setAdditionalServices}
                    setIsFormTouched={setIsFormTouched}
                  />
                </>
              )}
            </Box>
          )}
        </form>
        {formName === businessConnectContactCentreLandingPage && (
          <div
            className="container 85%"
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <Button
              type="button"
              onPress={handleCreateNewOrder}
              variant={{ priority: 'high' }}
              disabled={isSubmitting}
              style={{ marginBottom: '1em' }}
            >
              {isSubmitting ? submittingButtonLabel : newRequestButtonLabel}
            </Button>
          </div>
        )}
        {formName === businessConnectContactCentreOnlineTool && (
          <FormButtons
            isFormTouched={isFormTouched}
            submit={submit}
            saveAndExit={saveAndExit}
            showFooterError={showFooterError}
            setShowFooterError={setShowFooterError}
            isSubmitting={isSubmitting}
          />
        )}
      </div>

      <Modal
        isOpen={modalData.open}
        onClose={goToHomePage}
      >
        {modalData.title && (
          <>
            <div style={{ paddingRight: '2rem' }}>
              <Typography variant={{ size: 'h2' }}>{modalData.title}</Typography>
            </div>
            <Spacer space={1} />
          </>
        )}
        <p>{modalData.body}</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button
            type="button"
            variant={{ priority: 'low' }}
            onPress={goToHomePage}
          >
            {modalData.cancelText}
          </Button>
          <Button
            type="button"
            variant={{ priority: 'high' }}
            onPress={goToHomePage}
          >
            {modalData.confirmText}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

FormStepContactCentre.propTypes = {
  stepData: PropTypes.shape({
    fields: PropTypes.objectOf(PropTypes.object).isRequired
  }).isRequired,
  onFormSubmit: PropTypes.func.isRequired,
  onSaveAndExit: PropTypes.func.isRequired,
  orderData: PropTypes.objectOf(PropTypes.any),
  setOrderData: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  createNewContactOrder: PropTypes.func.isRequired,
  navigateToContactCentreHomePage: PropTypes.func.isRequired
};

FormStepContactCentre.defaultProps = {
  orderData: {}
};

export default FormStepContactCentre;
